import translations from "@/config/translations";
import useDashboardStore from "@/hooks/useDashboardStore";
import useFetch from "@/hooks/useFetch";
import useLangStore from "@/hooks/useLangStore";
import { useNavigate } from "react-router-dom";
import "./styles.css";

// ── Types ─────────────────────────────────────────────────────────────────────

interface Permission {
    store: string;
    model: string;
    actions: string[];
    branches: string[] | "all";
}

interface ClientData {
    _id: string;
    name: string;
    phone: string;
    email?: string;
    picture?: string;
    birthDate?: string;
    gender?: "male" | "female";
    owner: string[];
    permissions: Permission[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(dateString: string, lang: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString(lang === "ar" ? "ar-SA" : "en-US", {
        day: "numeric",
        month: "long",
        year: "numeric",
    });
}

function formatPhone(phone: string): string {
    if (!phone) return "";
    // Format: +966 50 327 2819
    const cleaned = phone.replace(/\D/g, "");
    if (cleaned.length === 12 && cleaned.startsWith("966")) {
        return `+${cleaned.slice(0, 3)} ${cleaned.slice(3, 5)} ${cleaned.slice(5, 8)} ${cleaned.slice(8)}`;
    }
    return phone;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function ProfileScreen() {
    const { lang } = useLangStore();
    const { storeId } = useDashboardStore();
    const t = translations[lang ?? "ar"];
    const navigate = useNavigate();

    const { data, loading } = useFetch("clients/me", { method: "GET" });
    const client: ClientData | null = data?.data?.client ?? null;

    const isOwner = client?.owner?.includes(storeId ?? "") ?? false;

    // Filter permissions for current store
    const storePermissions = client?.permissions?.filter((p) => p.store === storeId) ?? [];

    if (loading) {
        return (
            <div className="profile-screen profile-screen--loading">
                <div className="profile-spinner" />
            </div>
        );
    }

    if (!client) {
        return (
            <div className="profile-screen">
                <p className="profile-error">Unable to load profile</p>
            </div>
        );
    }

    const handleSettings = () => {
        navigate?.("/settings");
    };

    return (
        <div className="profile-screen">
            <div className="profile-card">
                {/* Avatar Section */}
                <div className="profile-avatar-section">
                    <div className="profile-avatar-wrap">
                        {client.picture ? (
                            <img src={client.picture} alt={client.name} className="profile-avatar-img" />
                        ) : (
                            <span className="profile-avatar-letter">{client.name?.charAt(0) ?? "?"}</span>
                        )}
                    </div>
                    <div className="profile-role-badge">{isOwner ? t.profile.owner : t.profile.staff}</div>
                </div>

                {/* Client Info */}
                <div className="profile-info">
                    <p className="profile-name">{client.name ?? "—"}</p>
                    {client.email && <p className="profile-email">{client.email}</p>}
                    <p className="profile-phone" dir="ltr">
                        {formatPhone(client.phone)}
                    </p>
                </div>

                {/* Extra Info Badges */}
                {(client.birthDate || client.gender) && (
                    <div className="profile-badges">
                        {client.gender && (
                            <div className="profile-badge">
                                <img src="/icons/gender-icon.svg" alt="" className="profile-badge-icon" />
                                <span>{client.gender === "male" ? t.profile.male : t.profile.female}</span>
                            </div>
                        )}
                        {client.birthDate && (
                            <div className="profile-badge">
                                <img src="/icons/calendar-icon.svg" alt="" className="profile-badge-icon" />
                                <span>{formatDate(client.birthDate, lang ?? "ar")}</span>
                            </div>
                        )}
                    </div>
                )}

                {/* Permissions Section */}
                {storePermissions.length > 0 && (
                    <div className="profile-permissions">
                        <h3 className="profile-permissions-title">{t.profile.permissions}</h3>
                        <div className="profile-permissions-grid">
                            {storePermissions.map((perm, idx) => (
                                <div key={idx} className="profile-permission-row">
                                    <span className="profile-permission-model">
                                        {(t.profile.models as Record<string, string>)[perm.model] ?? perm.model}
                                    </span>
                                    <div className="profile-permission-actions">
                                        {perm.actions.map((action) => (
                                            <span key={action} className="profile-permission-action">
                                                {(t.profile.actions as Record<string, string>)[action] ?? action}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Edit Button */}
                <button className="profile-edit-btn" onClick={handleSettings} type="button">
                    {t.profile.editAccount}
                </button>
            </div>
        </div>
    );
}
